import React from "react";
import PropTypes from "prop-types";
import { displayDateUS } from "../../../utils/dateUtils";
import {
  ADL_OPTIONS,
  IADL_OPTIONS,
  MOBILITY_OPTIONS,
  CONTINENCE_OPTIONS,
  BEHAVIOR_OPTIONS,
  COGNITIVE_OPTIONS,
  CLIENT_ASSESSMENT_TITLE,
} from "../assessmentForm.shared";
import { ASSESSMENT_PRINT_STYLES } from "../assessmentPrintStyles";
import {
  CheckboxSection,
  DualCheckboxColumns,
  ScheduleOfCareFields,
  HomeAssessmentFields,
  SafetyAssessmentFields,
} from "./AssessmentFieldKit";

const PrintSection = ({ title, children }) => (
  <section className="assessment-print-section break-inside-avoid border border-slate-300 rounded-md overflow-hidden mb-2 print:mb-1 print:rounded-sm">
    <div className="px-3 py-1 border-b border-slate-300 bg-slate-100 print:px-2 print:py-0.5">
      <h2 className="text-[11px] font-bold uppercase tracking-wide text-slate-800 print:text-[10px]">
        {title}
      </h2>
    </div>
    <div className="bg-white print:text-black">{children}</div>
  </section>
);

const EmptyLine = ({ text }) => (
  <p className="text-[12px] text-slate-500 italic px-3 py-2 print:px-2 print:py-1">{text}</p>
);

const AssessmentPrintSheet = ({ patient, payload, notes = [] }) => {
  const p = payload || {};
  const schedule = p.scheduleOfCare || {};
  const adl = p.adl || {};
  const iadl = p.iadl || {};
  const physical = p.physical || {};
  const mental = p.mental || {};
  const home = p.home || {};
  const safety = p.safety || { selected: [] };

  const patientName =
    [patient?.firstName, patient?.middleName, patient?.lastName]
      .filter(Boolean)
      .join(" ") || "Client";

  return (
    <>
      <style>{ASSESSMENT_PRINT_STYLES}</style>
      <article className="assessment-print-sheet mx-auto w-full max-w-4xl bg-white text-slate-900 px-4 sm:px-6 py-2 print:max-w-none print:px-0 print:py-0">
        <header className="mb-2 pb-2 border-b border-slate-300 print:mb-1 print:pb-1">
          <h1 className="text-base font-bold text-slate-900 print:text-sm">
            {CLIENT_ASSESSMENT_TITLE}
          </h1>
          <p className="text-[12px] text-slate-600 mt-0.5 print:text-[11px] print:mt-0">
            <span className="font-semibold text-slate-800">{patientName}</span>
            {" · "}
            MRN {patient?.mrn || "—"}
            {" · "}
            Printed {displayDateUS(new Date())}
          </p>
        </header>

        <PrintSection title="Schedule of Care">
          <ScheduleOfCareFields data={schedule} canEdit={false} printMode />
        </PrintSection>

        <PrintSection title="Activities of Daily Living (ADL)">
          <CheckboxSection
            options={ADL_OPTIONS}
            selected={adl.selected}
            otherDetails={adl.otherDetails}
            canEdit={false}
            columns={4}
            printMode
          />
        </PrintSection>

        <PrintSection title="Instrumental ADL (IADL)">
          <CheckboxSection
            options={IADL_OPTIONS}
            selected={iadl.selected}
            otherDetails={iadl.otherDetails}
            canEdit={false}
            columns={4}
            printMode
          />
        </PrintSection>

        <PrintSection title="Physical Assessment">
          <DualCheckboxColumns
            canEdit={false}
            printMode
            leftTitle="Mobility"
            rightTitle="Continence"
            left={{
              options: MOBILITY_OPTIONS,
              selected: physical.mobility?.selected,
              otherDetails: physical.mobility?.otherDetails,
            }}
            right={{
              options: CONTINENCE_OPTIONS,
              selected: physical.continence?.selected,
              otherDetails: physical.continence?.otherDetails,
            }}
          />
        </PrintSection>

        <PrintSection title="Mental Assessment">
          <DualCheckboxColumns
            canEdit={false}
            printMode
            leftTitle="Behavior"
            rightTitle="Cognitive"
            left={{
              options: BEHAVIOR_OPTIONS,
              selected: mental.behavior?.selected,
              otherDetails: mental.behavior?.otherDetails,
            }}
            right={{
              options: COGNITIVE_OPTIONS,
              selected: mental.cognitive?.selected,
              otherDetails: mental.cognitive?.otherDetails,
            }}
          />
        </PrintSection>

        <PrintSection title="Home Assessment">
          <HomeAssessmentFields data={home} canEdit={false} printMode />
        </PrintSection>

        <PrintSection title="Safety Assessment">
          <SafetyAssessmentFields data={safety} canEdit={false} printMode />
        </PrintSection>

        <PrintSection title="Notes">
          {!notes.length ? (
            <EmptyLine text="No notes yet" />
          ) : (
            <ul className="px-3 py-2 space-y-2 print:px-2 print:py-1">
              {notes.map((note) => {
                const body =
                  String(note.description || "").trim() ||
                  String(note.title || "").trim() ||
                  "—";
                const when =
                  displayDateUS(note.noteDate || note.createdAt) || "";
                const who = [
                  note.createdByName || null,
                  note.createdByRole ? `(${note.createdByRole})` : null,
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <li
                    key={note.id || `${note.createdAt}-${body.slice(0, 12)}`}
                    className="border-b border-slate-100 last:border-0 pb-2 last:pb-0 break-inside-avoid"
                  >
                    {note.title &&
                    String(note.title).trim() &&
                    String(note.title).trim() !==
                      String(note.description || "").trim() ? (
                      <p className="text-[11px] font-semibold text-slate-700">
                        {note.title}
                      </p>
                    ) : null}
                    <p className="text-[12px] text-slate-900 whitespace-pre-wrap break-words">
                      {body}
                    </p>
                    <p className="mt-0.5 text-[10px] text-slate-500">
                      {[when, who].filter(Boolean).join(" · ") || "—"}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </PrintSection>
      </article>
    </>
  );
};

AssessmentPrintSheet.propTypes = {
  patient: PropTypes.object,
  payload: PropTypes.object,
  notes: PropTypes.array,
};

export default AssessmentPrintSheet;
